import React, {createContext, useContext, useEffect, useState, ReactNode} from 'react';
import ImagesConfigInterface from 'interfaces/ImagesConfigInterface';

interface ConfigContextType {
    config: ImagesConfigInterface | null;
    loading: boolean;
    error: Error | null;
}

// eslint-disable-next-line no-undefined
const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

interface ConfigProviderProps {
    children: ReactNode;
}

export const ConfigProvider: React.FC<ConfigProviderProps> = ({children}) => {
    const [config, setConfig] = useState<ImagesConfigInterface | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        const fetchConfig = async() => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL as string;
                const response = await fetch(`${apiUrl}/api/v2/banner/load_config`, {
                    headers: {
                        Accept: 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch config: ${response.statusText}`);
                }

                const data = await response.json();
                setConfig(data);
            } catch (err) {
                setError(err instanceof Error ? err : new Error('Unknown error occurred'));
            } finally {
                setLoading(false);
            }
        };

        fetchConfig();
    }, []);

    return (
        <ConfigContext.Provider value={{config, error, loading}}>
            {children}
        </ConfigContext.Provider>
    );
};

export const useConfig = (): ConfigContextType => {
    const context = useContext(ConfigContext);

    // eslint-disable-next-line no-undefined
    if (context === undefined) {
        throw new Error('useConfig must be used within a ConfigProvider');
    }

    return context;
};
